import type {
  AsyncTaskProgressAction,
  AsyncTaskProposalAction,
} from '../types';

interface AsyncTaskFields {
  readonly type?: string;
  readonly subType?: string;
  readonly proposalId?: unknown;
  readonly runId?: unknown;
}

function readAsyncTaskFields(data: unknown): AsyncTaskFields | null {
  if (!data || typeof data !== 'object') {
    return null;
  }
  return data as AsyncTaskFields;
}

function isAsyncTaskType(fields: AsyncTaskFields): boolean {
  return fields.type == null || fields.type === 'async-task';
}

function hasNonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0;
}

/**
 * True when an async-task action should render the HITL proposal card.
 *
 * - Explicit `subType: 'progress'` always wins (never a proposal), even if a
 *   leftover `proposalId` is present on the payload.
 * - Requires a non-empty string `proposalId` (confirm/reject need it).
 * - Matches `subType: 'proposal'` or legacy persisted actions that omit
 *   `subType` but still carry `proposalId`.
 */
export function isAsyncTaskProposalAction(
  data: unknown
): data is AsyncTaskProposalAction {
  const action = readAsyncTaskFields(data);
  if (!action || !isAsyncTaskType(action) || action.subType === 'progress') {
    return false;
  }
  if (!hasNonEmptyString(action.proposalId)) {
    return false;
  }
  return action.subType === 'proposal' || action.subType == null;
}

/**
 * True when an async-task action should render the live progress card.
 *
 * Mutually exclusive with {@link isAsyncTaskProposalAction}: if the proposal
 * guard matches (including legacy payloads that omit `subType` but carry a
 * non-empty `proposalId`), this returns false even when `runId` is also set.
 */
export function isAsyncTaskProgressAction(
  data: unknown
): data is AsyncTaskProgressAction {
  // Keep the two guards exclusive for any call order — never both true.
  if (isAsyncTaskProposalAction(data)) {
    return false;
  }
  const action = readAsyncTaskFields(data);
  if (!action || !isAsyncTaskType(action) || action.subType === 'proposal') {
    return false;
  }
  if (!hasNonEmptyString(action.runId)) {
    return false;
  }
  return action.subType === 'progress' || action.subType == null;
}
